/* ============================================================
   BOUFF — Slide-out Cart Drawer engine
   - Fully renders from /cart.js on every mutation
   - One-time purchases only (subscription flow handled by Seal)
   - No hardcoded variant or selling-plan IDs (fetched live)
   ============================================================ */
(function () {
  'use strict';

  var drawer, overlay, body, countEl, shipbarEl, bannerEl, linesEl,
      nudgeMount, upsellMount, footEl, closeBtn;

  var CFG = { subHandle: 'the-daily-boost', upsellTag: 'cart-upsell-onetime',
              freeShip: 8998, subPrice: 3999, subPercent: 38 };

  var busy = false;          // guards concurrent mutations
  var upsellProduct = null;  // cached tagged upsell product
  var compareCache = {};     // variantId -> compare_at_price (cents); cart.js lacks it

  /* ── helpers ─────────────────────────────────────────────── */
  function money(cents) { return '$' + (Number(cents) / 100).toFixed(2); }
  function moneyStr(dollars) { return '$' + parseFloat(dollars).toFixed(2); }
  var TAG_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
  function pill(text) { return '<span class="bcd-pill">' + TAG_SVG + escapeHtml(text) + '</span>'; }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── open / close ────────────────────────────────────────── */
  function open() {
    if (!drawer) return;
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refresh();
    if (closeBtn) closeBtn.focus();
  }
  function close() {
    if (!drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ── cart fetch + render ─────────────────────────────────── */
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* cart.js has no compare_at_price — fetch it from each product's .js (cents) and cache by variant. */
  function ensureCompareData(cart) {
    var handles = {};
    cart.items.forEach(function (it) {
      if (it.handle && compareCache[it.id] === undefined) handles[it.handle] = true;
    });
    var list = Object.keys(handles);
    if (!list.length) return Promise.resolve();
    return Promise.all(list.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (prod) {
          (prod.variants || []).forEach(function (v) {
            compareCache[v.id] = (v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : 0;
          });
        })
        .catch(function () {});
    }));
  }

  function refresh() {
    return getCart().then(function (cart) {
      return ensureCompareData(cart).then(function () { render(cart); return cart; });
    });
  }

  function render(cart) {
    if (countEl) countEl.textContent = cart.item_count;
    syncHeaderBadge(cart.item_count);

    updateShippingBar(cart);
    renderBanner(cart);
    renderLines(cart);
    renderNudge(cart);
    renderUpsell(cart);
    renderFooter(cart);
  }

  /* ── Part 3: free shipping bar ───────────────────────────── */
  function updateShippingBar(cart) {
    /* Gruns-style: always a light-background bar with a green progress fill.
       Unlocked/subscription = 100% filled; under $90 = partial. States mutually exclusive. */
    var hasSubscription = cart.items.some(function (it) { return !!it.selling_plan_allocation; });
    /* FIX 1: gauge against the cart SUBTOTAL only (items_subtotal_price — pre-shipping,
       pre-tax), never total_price. Threshold single-sourced from CFG.freeShip ($89.98 —
       shows a clean "Add $39.99 more" for a single $49.99 jar). */
    var subtotal = cart.items_subtotal_price / 100;
    var threshold = CFG.freeShip / 100;

    var text, pct;
    if (hasSubscription) {
      /* Scenario 1 (pure sub) vs Scenario 4 (mixed) — both always free. Two lines:
         headline on top, "Included with…" note underneath. */
      var hasOneTime = cart.items.some(function (it) { return !it.selling_plan_allocation; });
      var subNote = hasOneTime ? 'Included with your subscription' : 'Included with every subscription order';
      text = '🎉 You\'ve unlocked <b>FREE Shipping!</b><br><span class="bcd-shipbar-sub">' + subNote + '</span>';
      pct = 100;
    } else if (subtotal >= threshold) {
      text = '🎉 You\'ve unlocked <b>FREE Shipping!</b>';
      pct = 100;
    } else {
      text = 'Add <b>$' + (threshold - subtotal).toFixed(2) + '</b> more for FREE Shipping';
      pct = Math.min(100, Math.round((subtotal / threshold) * 100));
    }

    var full = pct >= 100;
    shipbarEl.className = 'bcd-shipbar bcd-shipbar--progress' + (full ? ' bcd-shipbar--won' : '');
    shipbarEl.innerHTML =
      '<p class="bcd-shipbar-text">' + text + '</p>' +
      '<div class="bcd-shipbar-track"><div class="bcd-shipbar-fill' + (full ? ' bcd-shipbar-fill--full' : '') + '" style="width:' + pct + '%"></div></div>';
  }

  /* ── Part 4: discount banner ─────────────────────────────── */
  function renderBanner(cart) {
    /* Show whenever there's any saving — a real discount, or compare-at / subscription pricing. */
    var originalTotal = 0;
    cart.items.forEach(function (it) {
      originalTotal += Math.max(compareCache[it.id] || 0, it.original_price) * it.quantity;
    });
    var has = (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) ||
              cart.total_discount > 0 || originalTotal > cart.total_price;
    if (cart.items.length && has) {
      bannerEl.className = 'bcd-discount-banner';
      bannerEl.innerHTML = 'Limited Time Discount Auto-Applied ✓';
      bannerEl.style.display = '';
    } else {
      bannerEl.innerHTML = '';
      bannerEl.style.display = 'none';
    }
  }

  /* ── Part 5: line items ──────────────────────────────────── */
  function renderLines(cart) {
    if (!cart.items.length) {
      linesEl.innerHTML =
        '<div class="bcd-empty">' +
          '<div class="bcd-empty__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg></div>' +
          '<h2 class="bcd-empty__title">Your cart is empty</h2>' +
          '<p class="bcd-empty__body">Let\'s find your pup\'s perfect match.</p>' +
          '<a href="/#shop" class="bcd-empty__cta">Shop The Daily Boost</a>' +
        '</div>';
      return;
    }

    var html = '';
    cart.items.forEach(function (item, i) {
      var isSub = !!item.selling_plan_allocation;
      var typeLabel = isSub
        ? (item.selling_plan_allocation.selling_plan.name || 'Subscription')
        : 'One-Time Purchase';

      /* Strikethrough + savings: prefer real discounts (cart.js), else compare_at_price.
         Totals = unit × quantity, so they multiply on every qty change.
         Savings % is unit-based, so it stays constant regardless of quantity. */
      var compareUnit = 0, compareTotal = 0;
      if (item.original_line_price > item.final_line_price) {
        compareUnit = item.original_price;
        compareTotal = item.original_line_price;
      } else if (compareCache[item.id] && compareCache[item.id] > item.price) {
        compareUnit = compareCache[item.id];
        compareTotal = compareUnit * item.quantity;
      }

      var compareHtml = compareTotal
        ? '<span class="bcd-line__compare">' + money(compareTotal) + '</span>'
        : '';

      var pillHtml = '';
      if (item.discounts && item.discounts.length) {
        pillHtml = '<div class="bcd-line__pill-row">' +
          item.discounts.map(function (d) { return pill(d.title); }).join(' ') + '</div>';
      } else if (compareUnit && compareUnit > item.price) {
        var savePct = Math.round((1 - item.price / compareUnit) * 100);
        pillHtml = '<div class="bcd-line__pill-row">' + pill('Save ' + savePct + '%') + '</div>';
      }

      var line = parseInt(i, 10) + 1;
      var minusDisabled = item.quantity <= 1 ? 'disabled' : '';
      var minusIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      var plusIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      var trashIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>';

      html +=
        '<div class="bcd-line" data-key="' + escapeHtml(item.key) + '">' +
          '<div class="bcd-line__img"><img src="' + (item.image ? escapeHtml(item.image) : '') + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy"></div>' +
          '<div class="bcd-line__main">' +
            '<p class="bcd-line__name">' + escapeHtml(item.product_title) + '</p>' +
            '<p class="bcd-line__type">' + escapeHtml(typeLabel) + '</p>' +
            pillHtml +
            '<div class="bcd-line__bottom">' +
              '<div class="bcd-qty">' +
                '<button type="button" class="bcd-qty__btn" data-act="dec" ' + minusDisabled + ' aria-label="Decrease quantity">' + minusIcon + '</button>' +
                '<span class="bcd-qty__num">' + item.quantity + '</span>' +
                '<button type="button" class="bcd-qty__btn" data-act="inc" aria-label="Increase quantity">' + plusIcon + '</button>' +
              '</div>' +
              '<div class="bcd-line__price-wrap">' + compareHtml +
                '<span class="bcd-line__price">' + money(item.final_line_price) + '</span>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="bcd-line__trash" data-act="remove" aria-label="Remove ' + escapeHtml(item.product_title) + '">' + trashIcon + '</button>' +
        '</div>';
    });
    linesEl.innerHTML = html;
  }

  /* ── Part 6: subscribe upgrade nudge ─────────────────────── */
  function renderNudge(cart) {
    /* Subscribe upsell nudge temporarily disabled — remove the next 2 lines to re-enable. */
    nudgeMount.innerHTML = '';
    return;
    var hasOneTime = cart.items.some(function (it) { return !it.selling_plan_allocation; });
    var hasSub = cart.items.some(function (it) { return !!it.selling_plan_allocation; });
    if (hasOneTime && !hasSub) {
      nudgeMount.innerHTML =
        '<button type="button" class="bcd-nudge" id="bcd-nudge">' +
          '<span class="bcd-nudge__txt">' +
            '<span class="bcd-nudge__t1">Subscribe &amp; Save ' + CFG.subPercent + '% Instead</span>' +
            '<span class="bcd-nudge__t2">Lock in ' + money(CFG.subPrice) + '/mo · Cancel anytime</span>' +
          '</span>' +
          '<span class="bcd-nudge__chev"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>' +
        '</button>';
    } else {
      nudgeMount.innerHTML = '';
    }
  }

  /* ── Part 7: in-cart upsell ──────────────────────────────── */
  function renderUpsell(cart) {
    /* Show whenever there's exactly 1 one-time item (no selling plan). Re-appears if the
       quantity drops back to 1. The "2nd jar" increments the existing line 1 -> 2. */
    var item = cart.items[0];
    if (cart.item_count !== 1 || !item || item.selling_plan_allocation) {
      upsellMount.innerHTML = '';
      return;
    }

    var priceStr = money(item.final_price);
    var img = item.image ? escapeHtml(item.image) : '';
    upsellMount.innerHTML =
      '<div class="bcd-upsell-card">' +
        '<p class="bcd-upsell-head">Stock Up &amp; Save</p>' +
        '<p class="bcd-upsell-reason">Most dog parents reorder within 2 weeks — never run out.</p>' +
        '<div class="bcd-upsell-top">' +
          '<div class="bcd-upsell-img"><img src="' + img + '" alt="The Daily Boost — 2nd Jar"></div>' +
          '<div class="bcd-upsell-body">' +
            '<p class="bcd-upsell-name">The Daily Boost — 2nd Jar</p>' +
            '<p class="bcd-upsell-meta">One-Time · No commitment</p>' +
            '<p class="bcd-upsell-price2">' + priceStr + '</p>' +
            '<button type="button" class="bcd-upsell-add" id="bcd-upsell-add" data-key="' + escapeHtml(item.key) + '">Add a 2nd Jar →</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function loadUpsellProduct() {
    if (upsellProduct !== null) return Promise.resolve(upsellProduct);
    return fetch('/products.json?limit=250', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var list = (data && data.products) || [];
        var match = list.filter(function (p) {
          return p.tags && p.tags.indexOf(CFG.upsellTag) !== -1 && p.variants && p.variants.length;
        })[0];
        if (!match) { upsellProduct = false; return false; }
        upsellProduct = {
          title: match.title,
          image: (match.images && match.images[0]) ? match.images[0].src : '',
          variant: match.variants[0]
        };
        return upsellProduct;
      })
      .catch(function () { upsellProduct = false; return false; });
  }

  /* ── Part 8: footer ──────────────────────────────────────── */
  function renderFooter(cart) {
    if (!cart.items.length) { footEl.innerHTML = ''; footEl.style.display = 'none'; return; }
    footEl.style.display = '';

    var n = cart.item_count;

    /* "Was" total = sum of each unit's highest of compare_at / pre-discount price × qty.
       Captures both compare_at savings and real discounts. */
    var originalTotal = 0;
    cart.items.forEach(function (it) {
      var wasUnit = Math.max(compareCache[it.id] || 0, it.original_price);
      originalTotal += wasUnit * it.quantity;
    });

    var pills = '';
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
      pills = '<div class="bcd-foot__pills">' +
        cart.cart_level_discount_applications.map(function (d) { return pill(d.title); }).join('') +
      '</div>';
    }

    /* FREE if any subscription line, or the cart SUBTOTAL (items_subtotal_price) meets
       the $90 threshold. Shipping cost itself isn't in /cart.js — it's only known at
       checkout — so otherwise we can only say "Calculated at checkout". */
    var footHasSub = cart.items.some(function (it) { return !!it.selling_plan_allocation; });
    var shipVal = (footHasSub || cart.items_subtotal_price >= CFG.freeShip)
      ? '<span class="bcd-foot__free">FREE</span>'
      : '<span>Calculated at checkout</span>';

    footEl.innerHTML =
      '<div class="bcd-foot__row"><span>Subtotal (' + n + ' item' + (n === 1 ? '' : 's') + ')</span>' +
        '<span class="bcd-foot__sub-amt">' + money(cart.items_subtotal_price) + '</span></div>' +
      pills +
      '<div class="bcd-foot__row"><span>Shipping</span>' + shipVal + '</div>' +
      '<hr class="bcd-foot__divider">' +
      '<div class="bcd-foot__total"><span class="bcd-foot__total-label">Total</span><span>' +
        (originalTotal > cart.total_price ? '<span class="bcd-total-orig">' + money(originalTotal) + '</span>' : '') +
        '<span class="bcd-foot__total-amt">' + money(cart.total_price) + '</span></span></div>' +
      '<a href="/checkout" class="bcd-checkout">Secure Checkout 🔒</a>' +
      '<div class="bcd-trust"><span>★ 1,000+ Reviews</span><span>Pause or cancel anytime</span><span>30-Day Guarantee</span></div>';
  }

  /* ── mutations ───────────────────────────────────────────── */
  function changeQty(key, qty) {
    if (busy) return;
    busy = true;
    fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); })
      .then(function (cart) { return ensureCompareData(cart).then(function () { render(cart); }); })
      .catch(function () { return refresh(); })
      .then(function () { busy = false; });
  }

  function addVariant(id, sellingPlan) {
    var item = { id: id, quantity: 1 };
    if (sellingPlan) item.selling_plan = sellingPlan;
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(item)
    });
  }

  function doNudgeSwap() {
    if (busy) return;
    busy = true;
    var nudgeBtn = document.getElementById('bcd-nudge');
    if (nudgeBtn) nudgeBtn.disabled = true;

    getCart().then(function (cart) {
      var oneTime = cart.items.filter(function (it) { return !it.selling_plan_allocation; })[0];
      if (!oneTime) { busy = false; return refresh(); }
      var variantId = oneTime.id;
      return fetch('/products/' + CFG.subHandle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (prod) {
          var planId = (prod.selling_plans && prod.selling_plans[0]) ? prod.selling_plans[0].id
                     : (prod.variants && prod.variants[0] && prod.variants[0].selling_plan_allocations &&
                        prod.variants[0].selling_plan_allocations[0]
                        ? prod.variants[0].selling_plan_allocations[0].selling_plan_id : null);
          return fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: oneTime.key, quantity: 0 })
          }).then(function () { return addVariant(variantId, planId); });
        });
    }).then(function () { return refresh(); })
      .catch(function () { return refresh(); })
      .then(function () { busy = false; });
  }

  function doUpsellAdd(key) {
    if (busy) return;
    busy = true;
    var btn = document.getElementById('bcd-upsell-add');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    /* Increment the existing one-time line 1 -> 2. Uses /cart/change.js on the line key —
       no product add and NO selling_plan, so the 2nd jar can never become a subscription. */
    fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 2 })
    }).then(function (r) { return r.json(); })
      .then(function (cart) {
        return ensureCompareData(cart).then(function () { render(cart); });
      })
      .catch(function () { /* fail silently */ })
      .then(function () { busy = false; });
  }

  /* ── header cart badge sync ──────────────────────────────── */
  function syncHeaderBadge(count) {
    var badge = document.querySelector('.hdr-cart__badge');
    if (badge) badge.textContent = count;
  }

  /* ── event delegation ────────────────────────────────────── */
  function onBodyClick(e) {
    var line = e.target.closest('.bcd-line');
    if (line) {
      var actBtn = e.target.closest('[data-act]');
      if (actBtn) {
        var key = line.getAttribute('data-key');
        var qtyEl = line.querySelector('.bcd-qty__num');
        var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
        var act = actBtn.getAttribute('data-act');
        if (act === 'inc') changeQty(key, qty + 1);
        else if (act === 'dec') { if (qty > 1) changeQty(key, qty - 1); }
        else if (act === 'remove') changeQty(key, 0);
        return;
      }
    }
    if (e.target.closest('#bcd-nudge')) { doNudgeSwap(); return; }
    var addBtn = e.target.closest('#bcd-upsell-add');
    if (addBtn) { doUpsellAdd(addBtn.getAttribute('data-key')); return; }
  }

  /* ── init ────────────────────────────────────────────────── */
  function init() {
    drawer = document.getElementById('bcd-drawer');
    overlay = document.getElementById('bcd-overlay');
    if (!drawer || !overlay) return;
    body = document.getElementById('bcd-body');
    countEl = document.getElementById('bcd-count');
    shipbarEl = document.getElementById('bcd-shipbar');
    bannerEl = document.getElementById('bcd-discount-banner');
    linesEl = document.getElementById('bcd-lines');
    nudgeMount = document.getElementById('bcd-nudge-mount');
    upsellMount = document.getElementById('bcd-upsell-mount');
    footEl = document.getElementById('bcd-foot');
    closeBtn = document.getElementById('bcd-close');

    CFG.subHandle = drawer.getAttribute('data-subscribe-handle') || CFG.subHandle;
    CFG.upsellTag = drawer.getAttribute('data-upsell-tag') || CFG.upsellTag;
    CFG.freeShip = parseInt(drawer.getAttribute('data-free-ship-threshold'), 10) || CFG.freeShip;
    CFG.subPrice = parseInt(drawer.getAttribute('data-sub-price'), 10) || CFG.subPrice;
    CFG.subPercent = parseInt(drawer.getAttribute('data-sub-percent'), 10) || CFG.subPercent;

    overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    drawer.addEventListener('click', onBodyClick);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Replace the legacy header drawer triggers so the cart icon opens this drawer.
    window.openCartDrawer = function (e) { if (e && e.preventDefault) e.preventDefault(); open(); };
    window.closeCartDrawer = close;
    window.BouffCart = { open: open, close: close, refresh: refresh };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
